interface PotIllustrationPlant {
  id: string;
  color?: string;
  species?: { color: string } | null;
}

interface PotIllustrationProps {
  shape: string;
  plants: PotIllustrationPlant[];
  size?: "small" | "normal" | "large";
}

const DEFAULT_LEAF_COLOR = "oklch(0.55 0.09 145)";

const POT_COLORS: Record<string, { body: string; rim: string; shadow: string }> = {
  terracotta: { body: "oklch(0.55 0.13 40)", rim: "oklch(0.6 0.13 40)",  shadow: "oklch(0.42 0.12 40)" },
  basket:     { body: "oklch(0.55 0.07 70)", rim: "oklch(0.62 0.06 70)", shadow: "oklch(0.42 0.07 70)" },
  concrete:   { body: "oklch(0.7 0.01 90)",  rim: "oklch(0.78 0.01 90)", shadow: "oklch(0.55 0.01 90)" },
  long:       { body: "oklch(0.5 0.12 40)",  rim: "oklch(0.58 0.12 40)", shadow: "oklch(0.4 0.12 40)" },
};

export function PotIllustration({ shape, plants, size = "normal" }: PotIllustrationProps) {
  const w = size === "large" ? 160 : size === "small" ? 70 : 110;
  const h = size === "large" ? 140 : size === "small" ? 64 : 100;
  const c = POT_COLORS[shape] ?? POT_COLORS["terracotta"]!;
  const isLong = shape === "long";
  const potW = isLong ? 100 : 70;
  const potX = (110 - potW) / 2;
  const potTop = 50;
  const potBottom = 95;
  const rimH = 6;
  const maxPlants = isLong ? 5 : 3;
  const visible = plants.slice(0, maxPlants);
  const total = visible.length;

  return (
    <div className="pot-illustration" style={{ width: w, height: h }}>
      <svg viewBox="0 0 110 100" width={w} height={h}>
        {visible.map((p, i) => {
          const spread = isLong ? 70 : 40;
          const x = 55 + (i - (total - 1) / 2) * (spread / Math.max(total, 1));
          const offset = (i % 2 === 0) ? 0 : 4;
          const leaf = p.species?.color ?? p.color ?? DEFAULT_LEAF_COLOR;
          return (
            <g key={p.id}>
              <ellipse cx={x} cy={potTop + 2} rx="8" ry="3" fill={leaf} opacity="0.3" />
              <path d={`M${x} ${potTop} Q ${x - 7} ${potTop - 18 - offset} ${x - 4} ${potTop - 30 - offset}`}
                    stroke={leaf} strokeWidth="2" fill="none" strokeLinecap="round" />
              <path d={`M${x} ${potTop} Q ${x + 7} ${potTop - 16 - offset} ${x + 5} ${potTop - 28 - offset}`}
                    stroke={leaf} strokeWidth="2" fill="none" strokeLinecap="round" />
              <ellipse cx={x - 4} cy={potTop - 32 - offset} rx="5" ry="8"
                       fill={leaf} transform={`rotate(-25 ${x - 4} ${potTop - 32 - offset})`} />
              <ellipse cx={x + 5} cy={potTop - 30 - offset} rx="5" ry="8"
                       fill={leaf} transform={`rotate(25 ${x + 5} ${potTop - 30 - offset})`} />
            </g>
          );
        })}
        <ellipse cx="55" cy={potTop} rx={potW / 2 - 2} ry="4" fill="oklch(0.32 0.05 60)" />
        <path d={`M${potX} ${potTop}
                  L${potX + 4} ${potBottom}
                  Q 55 ${potBottom + 3} ${potX + potW - 4} ${potBottom}
                  L${potX + potW} ${potTop} Z`}
              fill={c.body} />
        <rect x={potX - 2} y={potTop - rimH} width={potW + 4} height={rimH} rx="1.5" fill={c.rim} />
        <ellipse cx="55" cy={potTop - rimH + 1} rx={potW / 2} ry="2" fill={c.shadow} opacity="0.5" />
        <line x1={potX + 6} y1={potTop + 4} x2={potX + 8} y2={potBottom - 4}
              stroke="rgba(255,255,255,.18)" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </div>
  );
}
